import { ChatChip } from '../work/ChipManager';
import { diag } from '../../utils/trailhead_diag';

export const PERSONA_CHIP_STORAGE_KEY = 'tpp-persona-chat-chips';

// 2026-05-10 — defaults emptied per operator directive ("chips on a
// fresh load when workshop is closed = wtf"). Fresh load now yields an
// empty chip strip; users opt-in via ChipManager. Existing storage is
// preserved (we don't auto-clear), and the migration code below still
// handles label renames + future additions when defaults are revived.
//
// Historical 9-chip default lineup (Capabilities / Catch Me Up /
// Imagine That? / Hot Take / What Am I Missing? / Socratic Probe /
// 2nd Opinion / Visualize / Wrap Up) was removed in commit landing
// alongside this change — recover from `git log -p` if needed.
export const DEFAULT_PERSONA_CHIPS: ChatChip[] = [];

// Rename map for the 2026-04-24 chip-surgery pass. Migration is polite:
// the label is only updated when the stored chip still has the OLD
// default label (so a user who customized "🧠 Mental Model" → "🧠 My
// Brain" doesn't get clobbered by the rename).
const CHIP_RENAME_MAP: Record<
  string,
  { matchOldLabel: string; newLabel: string }
> = {
  'persona-mental-model': {
    matchOldLabel: '🧠 Mental Model',
    newLabel: '🧠 Imagine That?',
  },
  'persona-context-check': {
    matchOldLabel: '🔍 Context Check',
    newLabel: '🔍 Catch Me Up',
  },
  'persona-handoff': {
    matchOldLabel: '📝 Handoff Note',
    newLabel: '📝 Brief Next Person',
  },
  'persona-wrap': {
    matchOldLabel: '🏁 Wrap & Ship',
    newLabel: '🏁 Wrap Up',
  },
};

export function loadPersonaChips(): ChatChip[] {
  try {
    const stored = localStorage.getItem(PERSONA_CHIP_STORAGE_KEY);
    if (stored) {
      const parsed: ChatChip[] = JSON.parse(stored);

      // Polite rename: only update label if the stored label still
      // matches the prior default. Custom user labels are preserved.
      let didChange = false;
      const renamed = parsed.map((c) => {
        const rename = CHIP_RENAME_MAP[c.id];
        if (rename && c.label === rename.matchOldLabel) {
          didChange = true;
          return { ...c, label: rename.newLabel };
        }
        return c;
      });

      // Append new default chips not present in storage. New chips land
      // at the end so the user's existing order is preserved. (Currently
      // a no-op since DEFAULT_PERSONA_CHIPS is empty per 2026-05-10.)
      const existingIds = new Set(renamed.map((c) => c.id));
      const additions = DEFAULT_PERSONA_CHIPS.filter(
        (d) => !existingIds.has(d.id),
      );
      const final = additions.length
        ? [...renamed, ...additions.map((c) => ({ ...c }))]
        : renamed;
      if (additions.length) didChange = true;

      if (didChange) {
        try {
          localStorage.setItem(
            PERSONA_CHIP_STORAGE_KEY,
            JSON.stringify(final),
          );
        } catch {
          // localStorage unavailable — return migrated chips in-memory
          // anyway so the session sees the new lineup.
        }
      }
      diag({
        surface: 'chips',
        event: 'load · from storage',
        detail: {
          rawLen: stored.length,
          stored: parsed.length,
          renamed: renamed.length,
          appended: additions.length,
          final: final.length,
          ids: final.map((c) => c.id),
        },
      });
      return final;
    }
  } catch (err) {
    diag({
      surface: 'chips',
      event: 'load · storage parse error · falling back to defaults',
      detail: { err: String(err) },
    });
  }
  const fallback = DEFAULT_PERSONA_CHIPS.map((c) => ({ ...c }));
  diag({
    surface: 'chips',
    event: 'load · defaults',
    detail: { count: fallback.length },
  });
  return fallback;
}

export function savePersonaChips(chips: ChatChip[]) {
  localStorage.setItem(PERSONA_CHIP_STORAGE_KEY, JSON.stringify(chips));
  diag({
    surface: 'chips',
    event: 'save · localStorage',
    detail: { count: chips.length, ids: chips.map((c) => c.id) },
  });
}
